import type {
  SpendAnalyticsBasis,
  SpendAnalyticsGranularity,
  SpendAnalyticsGroupBy,
  VendorSpendAnalyticsResponse,
} from '@/services/vendorDetails'

export type SpendTransactionType = 'bill' | 'purchase' | 'vendor_credit'

export interface SpendTransactionLine {
  amount: number
  category: string
  location: string
}

export interface SpendTransaction {
  type: SpendTransactionType
  id: string
  txn_date: string
  total_amt: number
  lines: SpendTransactionLine[]
}

function txnDateInRange(txnDate: string, startDate: string, endDate: string): boolean {
  if (!txnDate) return false
  return startDate <= txnDate && txnDate <= endDate
}

function periodKey(txnDate: string, granularity: SpendAnalyticsGranularity): string {
  const parts = txnDate.split('-').map(Number)
  const year = parts[0] ?? 0
  const month = parts[1] ?? 1
  if (granularity === 'month') {
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`
  }
  if (granularity === 'quarter') {
    const quarter = Math.floor((month - 1) / 3) + 1
    return `${String(year).padStart(4, '0')}-Q${quarter}`
  }
  return `${String(year).padStart(4, '0')}`
}

function accumulateLineSegments(
  txn: SpendTransaction,
  segmentsGross: Record<string, number>,
  segmentsNet: Record<string, number>,
  segmentExtractor: (line: SpendTransactionLine) => string,
  sign = 1
): void {
  for (const line of txn.lines) {
    const segment = segmentExtractor(line)
    const amount = line.amount * sign
    if (sign > 0) {
      segmentsGross[segment] = (segmentsGross[segment] ?? 0) + amount
    }
    segmentsNet[segment] = (segmentsNet[segment] ?? 0) + amount
  }
}

export function computeVendorSpendAnalytics(
  transactions: SpendTransaction[],
  vendorId: string,
  displayName: string,
  startDate: string,
  endDate: string,
  granularity: SpendAnalyticsGranularity,
  basis: SpendAnalyticsBasis,
  groupBy: SpendAnalyticsGroupBy
): VendorSpendAnalyticsResponse {
  const segmentExtractor =
    groupBy === 'location'
      ? (line: SpendTransactionLine) => line.location
      : (line: SpendTransactionLine) => line.category

  const periodGross: Record<string, number> = {}
  const periodNet: Record<string, number> = {}
  const periodSegmentsGross: Record<string, Record<string, number>> = {}
  const periodSegmentsNet: Record<string, Record<string, number>> = {}
  const globalSegmentsGross: Record<string, number> = {}
  const globalSegmentsNet: Record<string, number> = {}

  let totalGross = 0
  let totalNet = 0
  let billCount = 0
  let vendorCreditCount = 0
  const expenseTxnIds = new Set<string>()

  const ensurePeriodSegments = (
    period: string
  ): { gross: Record<string, number>; net: Record<string, number> } => {
    let gross = periodSegmentsGross[period]
    if (!gross) {
      gross = {}
      periodSegmentsGross[period] = gross
    }
    let net = periodSegmentsNet[period]
    if (!net) {
      net = {}
      periodSegmentsNet[period] = net
    }
    return { gross, net }
  }

  for (const txn of transactions) {
    if (!txn.id || !txnDateInRange(txn.txn_date, startDate, endDate)) continue

    if (txn.type === 'bill') {
      const amount = txn.total_amt
      const period = periodKey(txn.txn_date, granularity)
      periodGross[period] = (periodGross[period] ?? 0) + amount
      periodNet[period] = (periodNet[period] ?? 0) + amount
      totalGross += amount
      totalNet += amount
      billCount += 1
      const segments = ensurePeriodSegments(period)
      accumulateLineSegments(txn, segments.gross, segments.net, segmentExtractor)
      accumulateLineSegments(txn, globalSegmentsGross, globalSegmentsNet, segmentExtractor)
      continue
    }

    if (txn.type === 'purchase') {
      const amount = txn.total_amt
      const period = periodKey(txn.txn_date, granularity)
      periodGross[period] = (periodGross[period] ?? 0) + amount
      periodNet[period] = (periodNet[period] ?? 0) + amount
      totalGross += amount
      totalNet += amount
      expenseTxnIds.add(txn.id)
      const segments = ensurePeriodSegments(period)
      accumulateLineSegments(txn, segments.gross, segments.net, segmentExtractor)
      accumulateLineSegments(txn, globalSegmentsGross, globalSegmentsNet, segmentExtractor)
      continue
    }

    if (txn.type === 'vendor_credit') {
      const amount = txn.total_amt
      const period = periodKey(txn.txn_date, granularity)
      periodNet[period] = (periodNet[period] ?? 0) - amount
      totalNet -= amount
      vendorCreditCount += 1
      const segments = ensurePeriodSegments(period)
      accumulateLineSegments(txn, segments.gross, segments.net, segmentExtractor, -1)
      accumulateLineSegments(
        txn,
        globalSegmentsGross,
        globalSegmentsNet,
        segmentExtractor,
        -1
      )
    }
  }

  const basisTotal = basis === 'net' ? totalNet : totalGross

  const allSegments = new Set([
    ...Object.keys(globalSegmentsGross),
    ...Object.keys(globalSegmentsNet),
  ])

  const segmentBreakdown = [...allSegments]
    .map((segment) => {
      const segGross = globalSegmentsGross[segment] ?? 0
      const segNet = globalSegmentsNet[segment] ?? 0
      const basisAmount = basis === 'net' ? segNet : segGross
      if (basisAmount === 0 && segGross === 0 && segNet === 0) return null
      const pct = basisTotal ? (basisAmount / basisTotal) * 100 : 0
      return { segment, gross: segGross, net: segNet, pct }
    })
    .filter((item): item is NonNullable<typeof item> => item != null)
    .sort(
      (a, b) =>
        Math.abs(basis === 'net' ? b.net : b.gross) -
        Math.abs(basis === 'net' ? a.net : a.gross)
    )

  const periodKeys = new Set([...Object.keys(periodGross), ...Object.keys(periodNet)])
  const periods = [...periodKeys]
    .sort()
    .map((period) => {
      const segs =
        basis === 'net' ? periodSegmentsNet[period] ?? {} : periodSegmentsGross[period] ?? {}
      const bySegment = Object.entries(segs)
        .filter(([, amount]) => amount !== 0)
        .sort(([, a], [, b]) => Math.abs(b) - Math.abs(a))
        .map(([segment, amount]) => ({ segment, amount }))
      return {
        period,
        gross: periodGross[period] ?? 0,
        net: periodNet[period] ?? 0,
        by_segment: bySegment,
      }
    })

  return {
    vendor_id: vendorId,
    display_name: displayName,
    start_date: startDate,
    end_date: endDate,
    granularity,
    basis,
    group_by: groupBy,
    totals: {
      gross: totalGross,
      net: totalNet,
      expense_txn_count: expenseTxnIds.size,
      bill_count: billCount,
      vendor_credit_count: vendorCreditCount,
    },
    periods,
    segment_breakdown: segmentBreakdown,
  }
}

const VENDOR_SPEND_EXPORT_CSV_HEADERS = [
  'section',
  'vendor_id',
  'display_name',
  'start_date',
  'end_date',
  'granularity',
  'basis',
  'group_by',
  'exported_at',
  'gross',
  'net',
  'expense_txn_count',
  'bill_count',
  'vendor_credit_count',
  'period',
  'period_gross',
  'period_net',
  'segment',
  'segment_amount',
  'pct_of_total',
  'txn_type',
  'txn_id',
  'txn_date',
  'total_amt',
  'line_amount',
  'category',
  'location',
] as const

export function escapeVendorSpendCsvCell(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function vendorSpendCsvRow(cells: string[]): string {
  return cells.map(escapeVendorSpendCsvCell).join(',')
}

type VendorSpendCsvRow = Partial<Record<typeof VENDOR_SPEND_EXPORT_CSV_HEADERS[number], string>>

function vendorSpendCsvRowFromSlots(slots: VendorSpendCsvRow): string {
  return vendorSpendCsvRow(
    VENDOR_SPEND_EXPORT_CSV_HEADERS.map((header) => slots[header] ?? '')
  )
}

export function sanitizeVendorSpendExportFilename(displayName: string): string {
  const sanitized = displayName.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '')
  return sanitized || 'vendor'
}

export function buildVendorSpendExportFilename(
  displayName: string,
  startDate: string,
  endDate: string
): string {
  const name = sanitizeVendorSpendExportFilename(displayName)
  return `vendor-spend-${name}-${startDate}-${endDate}.csv`
}

export function buildVendorSpendExportCsv(
  analytics: VendorSpendAnalyticsResponse,
  transactions: SpendTransaction[],
  exportedAt?: string
): string {
  const exportedAtIso = exportedAt ?? new Date().toISOString()
  const rows: string[] = [vendorSpendCsvRow([...VENDOR_SPEND_EXPORT_CSV_HEADERS])]

  rows.push(
    vendorSpendCsvRowFromSlots({
      section: 'meta',
      vendor_id: analytics.vendor_id,
      display_name: analytics.display_name,
      start_date: analytics.start_date,
      end_date: analytics.end_date,
      granularity: analytics.granularity,
      basis: analytics.basis,
      group_by: analytics.group_by,
      exported_at: exportedAtIso,
    })
  )

  rows.push(
    vendorSpendCsvRowFromSlots({
      section: 'totals',
      gross: String(analytics.totals.gross),
      net: String(analytics.totals.net),
      expense_txn_count: String(analytics.totals.expense_txn_count),
      bill_count: String(analytics.totals.bill_count),
      vendor_credit_count: String(analytics.totals.vendor_credit_count),
    })
  )

  for (const periodRow of analytics.periods) {
    if (periodRow.by_segment.length === 0) {
      rows.push(
        vendorSpendCsvRowFromSlots({
          section: 'period',
          period: periodRow.period,
          period_gross: String(periodRow.gross),
          period_net: String(periodRow.net),
        })
      )
      continue
    }

    for (const segmentRow of periodRow.by_segment) {
      rows.push(
        vendorSpendCsvRowFromSlots({
          section: 'period',
          period: periodRow.period,
          period_gross: String(periodRow.gross),
          period_net: String(periodRow.net),
          segment: segmentRow.segment,
          segment_amount: String(segmentRow.amount),
        })
      )
    }
  }

  for (const breakdownRow of analytics.segment_breakdown) {
    rows.push(
      vendorSpendCsvRowFromSlots({
        section: 'breakdown',
        gross: String(breakdownRow.gross),
        net: String(breakdownRow.net),
        segment: breakdownRow.segment,
        pct_of_total: String(breakdownRow.pct),
      })
    )
  }

  const { start_date, end_date } = analytics
  for (const txn of transactions) {
    if (!txn.id || !txnDateInRange(txn.txn_date, start_date, end_date)) continue

    if (txn.lines.length === 0) {
      rows.push(
        vendorSpendCsvRowFromSlots({
          section: 'transaction',
          txn_type: txn.type,
          txn_id: txn.id,
          txn_date: txn.txn_date,
          total_amt: String(txn.total_amt),
        })
      )
      continue
    }

    for (const line of txn.lines) {
      rows.push(
        vendorSpendCsvRowFromSlots({
          section: 'transaction',
          txn_type: txn.type,
          txn_id: txn.id,
          txn_date: txn.txn_date,
          total_amt: String(txn.total_amt),
          line_amount: String(line.amount),
          category: line.category,
          location: line.location,
        })
      )
    }
  }

  return rows.join('\n')
}

export function buildVendorSpendExport(
  analytics: VendorSpendAnalyticsResponse,
  transactions: SpendTransaction[],
  exportedAt?: string
): { filename: string; csvBody: string } {
  return {
    filename: buildVendorSpendExportFilename(
      analytics.display_name,
      analytics.start_date,
      analytics.end_date
    ),
    csvBody: buildVendorSpendExportCsv(analytics, transactions, exportedAt),
  }
}
